import type {
  EndSimulatedSipsimCallBody,
  PatchCallSummaryInstructionsPayload,
  UpdateCallPayload,
} from "@asap/shared/schemas/call.schema";
import { useApi } from "~/composables/useApi";

export const useCall = () => {
  const { invoke } = useApi();

  const getCall = async (id: string) => {
    const result = await invoke({ path: "calls/:callId", method: "GET", params: { callId: id } });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  };

  const updateCall = async (id: string, payload: UpdateCallPayload) => {
    const result = await invoke({ path: "calls/:callId", method: "PATCH", params: { callId: id }, body: payload });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  };

  const getOngoingCallForMe = async () => {
    const result = await invoke({ path: "calls/ongoing-for-me", method: "GET" });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  };

  // Outside production only: simulated SipSim calls
  const startSimulatedSipsimCall = async (talentId: string) => {
    const result = await invoke({ path: "dev/simulated-sipsim-calls", method: "POST", body: { talentId } });
    if (!result.success) throw new Error(result.error.clientMessage || result.error.message);
    return result.data;
  };

  const endSimulatedSipsimCall = async (pbxId: string, body: EndSimulatedSipsimCallBody) => {
    const result = await invoke({
      path: "dev/simulated-sipsim-calls/:pbxId/end",
      method: "POST",
      params: { pbxId },
      body,
    });
    if (!result.success) throw new Error(result.error.clientMessage || result.error.message);
    return result.data;
  };

  // TEMPORARY — playground SipSim proxy methods; remove when production endpoints land
  const getCallTranscription = async (externalCallId: string) => {
    const result = await invoke({
      path: "playground/sipsim/calls/:externalCallId/transcription",
      method: "GET",
      params: { externalCallId },
    });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  };

  const getCallSummary = async (externalCallId: string) => {
    const result = await invoke({
      path: "playground/sipsim/calls/:externalCallId/summary",
      method: "GET",
      params: { externalCallId },
    });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  };

  const createCallSummary = async (externalCallId: string) => {
    const result = await invoke({
      path: "playground/sipsim/calls/:externalCallId/summary",
      method: "POST",
      params: { externalCallId },
    });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  };

  const patchCallSummaryInstructions = async (externalCallId: string, payload: PatchCallSummaryInstructionsPayload) => {
    const result = await invoke({
      path: "playground/sipsim/calls/:externalCallId/summary",
      method: "PATCH",
      params: { externalCallId },
      body: payload,
    });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  };

  const getSummarySettings = async () => {
    const result = await invoke({ path: "playground/sipsim/settings/summary", method: "GET" });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  };

  return {
    getCall,
    updateCall,
    getOngoingCallForMe,
    startSimulatedSipsimCall,
    endSimulatedSipsimCall,
    getCallTranscription,
    getCallSummary,
    createCallSummary,
    patchCallSummaryInstructions,
    getSummarySettings,
  };
};
